/**
 * Time Clock Management Tab - Admin Panel
 * Manages staff time clock entries, device registrations, and anti-buddy punching
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Clock, 
  Smartphone,
  Wifi,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Play,
  Square,
  RefreshCw,
  Settings,
  Shield,
  User,
  Calendar,
  Timer,
  Bell,
  ChevronDown,
  ChevronUp,
  Search,
  Filter,
  FileText,
  Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Status badge colors
const STATUS_COLORS = {
  working: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', label: 'Working' },
  completed: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', label: 'Completed' },
  late: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', label: 'Late' },
  early: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', label: 'Early' }
};

export default function TimeClockTab() {
  const [todayEntries, setTodayEntries] = useState([]);
  const [allEntries, setAllEntries] = useState([]);
  const [devices, setDevices] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('today'); // today, history, payroll, devices, settings, alerts
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });
  const [searchQuery, setSearchQuery] = useState('');
  
  // Payroll report state
  const [payrollReport, setPayrollReport] = useState(null);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [payrollPeriod, setPayrollPeriod] = useState(() => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
      start: startOfMonth.toISOString().split('T')[0],
      end: endOfMonth.toISOString().split('T')[0]
    };
  });

  const fetchTodayEntries = useCallback(async () => {
    try {
      const response = await api.get('/workforce/timeclock/entries/today');
      setTodayEntries(response.data.entries || []);
    } catch (error) {
      console.error('Error fetching today entries:', error);
    }
  }, []);

  const fetchAllEntries = useCallback(async () => {
    try {
      const params = { limit: 100 };
      if (dateFilter.from) params.date_from = dateFilter.from;
      if (dateFilter.to) params.date_to = dateFilter.to;
      
      const response = await api.get('/workforce/timeclock/entries', { params });
      setAllEntries(response.data.entries || []);
    } catch (error) {
      console.error('Error fetching entries:', error);
    }
  }, [dateFilter]);

  const fetchDevices = useCallback(async () => {
    try {
      const response = await api.get('/workforce/timeclock/devices');
      setDevices(response.data.devices || []);
    } catch (error) {
      console.error('Error fetching devices:', error);
    }
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await api.get('/workforce/timeclock/alerts', { params: { limit: 50 } });
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.get('/workforce/timeclock/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, []);

  const fetchPayrollReport = useCallback(async () => {
    setPayrollLoading(true);
    try {
      const response = await api.get('/workforce/timeclock/payroll-report', { 
        params: { start_date: payrollPeriod.start, end_date: payrollPeriod.end } 
      });
      setPayrollReport(response.data);
    } catch (error) {
      console.error('Error fetching payroll report:', error);
    } finally {
      setPayrollLoading(false);
    }
  }, [payrollPeriod]);

  const downloadPayrollCSV = async () => {
    try {
      const res = await api.get('/workforce/timeclock/payroll-report/export', {
        params: { start_date: payrollPeriod.start, end_date: payrollPeriod.end },
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `payroll_${payrollPeriod.start}_to_${payrollPeriod.end}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading payroll CSV:', error);
      toast.error('Failed to download payroll report');
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchTodayEntries(),
      fetchDevices(),
      fetchAlerts(),
      fetchSettings()
    ]);
    setLoading(false);
  }, [fetchTodayEntries, fetchDevices, fetchAlerts, fetchSettings]);

  useEffect(() => {
    loadData();
    const interval = setInterval(fetchTodayEntries, 30000);
    return () => clearInterval(interval);
  }, [loadData, fetchTodayEntries]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchAllEntries();
    }
    if (activeTab === 'payroll') {
      fetchPayrollReport();
    }
  }, [activeTab, dateFilter, fetchAllEntries, fetchPayrollReport]);

  const handleDeactivateDevice = async (deviceId) => {
    if (!confirm('Are you sure you want to deactivate this device?')) return;
    
    try {
      const response = await api.post(`/workforce/timeclock/device/deactivate/${deviceId}`, null, {
        params: { reason: 'manual_deactivation' }
      });
      if (response.status === 200) {
        toast.success('Device deactivated');
        fetchDevices();
      } else {
        toast.error('Failed to deactivate device');
      }
    } catch (error) {
      toast.error('Error deactivating device');
    }
  };

  const handleDismissAlert = async (alertId) => {
    try {
      const response = await api.delete(`/workforce/timeclock/alerts/${alertId}`);
      if (response.status === 200) {
        setAlerts(alerts.filter(a => a.id !== alertId));
        toast.success('Alert dismissed');
      }
    } catch (error) {
      toast.error('Error dismissing alert');
    }
  };

  const handleClearDeviceRegistration = async (staffId, staffName) => {
    if (!window.confirm(`Clear device registration for ${staffName}? This will allow them to re-register their device.`)) {
      return;
    }
    
    try {
      const response = await api.delete(`/workforce/timeclock/devices/${staffId}`);
      if (response.status === 200) {
        toast.success(`Device registration cleared for ${staffName}. They can now re-register.`);
        fetchDevices();
        fetchAlerts();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error clearing device registration');
    }
  };

  const handleManagerOverride = async (action, staffId, reason, deviceData = {}) => {
    try {
      const response = await api.post('/workforce/timeclock/manager-override', {
        staff_id: staffId,
        action,
        reason,
        ...deviceData
      });
      if (response.status === 200) {
        toast.success('Override completed');
        loadData();
        setShowOverrideModal(false);
      } else {
        toast.error(response.data.detail || 'Override failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error processing override');
    }
  };

  const handleSaveSettings = async (newSettings) => {
    try {
      const response = await api.put('/workforce/timeclock/settings', newSettings);
      if (response.status === 200) {
        toast.success('Settings saved');
        fetchSettings();
        setShowSettingsModal(false);
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Error saving settings');
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatHours = (hours) => {
    if (!hours && hours !== 0) return '-';
    return `${hours.toFixed(2)} hrs`;
  };

  // Filter entries by search
  const filteredEntries = (activeTab === 'today' ? todayEntries : allEntries).filter(entry => {
    if (!searchQuery) return true;
    return entry.staff_name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Summary stats
  const summary = {
    currentlyWorking: todayEntries.filter(e => !e.clock_out_time).length,
    completedShifts: todayEntries.filter(e => e.clock_out_time).length,
    lateCount: todayEntries.filter(e => e.is_late).length,
    totalHours: todayEntries.reduce((sum, e) => sum + (e.paid_hours || e.current_hours || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Time Clock</h2>
          <p className="text-muted-foreground">Track staff clock-ins, manage devices, and view alerts</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            className="gap-2"
            data-testid="refresh-timeclock-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSettingsModal(true)}
            className="gap-2"
            data-testid="timeclock-settings-btn"
          >
            <Settings className="w-4 h-4" />
            Settings
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl p-4 border border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Play className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{summary.currentlyWorking}</p>
              <p className="text-sm text-muted-foreground">Working Now</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card rounded-xl p-4 border border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{summary.completedShifts}</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card rounded-xl p-4 border border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{summary.lateCount}</p>
              <p className="text-sm text-muted-foreground">Late Today</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card rounded-xl p-4 border border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Timer className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{summary.totalHours.toFixed(1)}</p>
              <p className="text-sm text-muted-foreground">Hours Today</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            <span className="font-medium text-amber-800 dark:text-amber-300">{alerts.length} Active Alert{alerts.length > 1 ? 's' : ''}</span>
          </div>
          <div className="space-y-2">
            {alerts.slice(0, 3).map(alert => (
              <div key={alert.id} className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg p-2">
                <span className="text-sm text-foreground">
                  {alert.type === 'late_clock_in' && `${alert.staff_name} clocked in ${alert.minutes_late}m late`}
                  {alert.type === 'duplicate_device' && `${alert.staff_name} tried to register device used by ${alert.existing_staff}`}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDismissAlert(alert.id)}
                  className="h-7 text-xs"
                >
                  Dismiss
                </Button>
              </div>
            ))}
          </div>
          {alerts.length > 3 && (
            <button
              onClick={() => setActiveTab('alerts')}
              className="text-sm text-amber-700 dark:text-amber-400 hover:underline mt-2"
            >
              View all {alerts.length} alerts
            </button>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-border overflow-x-auto pb-px">
        {[
          { id: 'today', label: 'Today', icon: Calendar },
          { id: 'history', label: 'History', icon: Clock },
          { id: 'payroll', label: 'Payroll Report', icon: FileText },
          { id: 'devices', label: 'Devices', icon: Smartphone },
          { id: 'alerts', label: 'Alerts', icon: Bell, badge: alerts.length },
          { id: 'settings', label: 'Settings', icon: Settings }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
              ${activeTab === tab.id 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            data-testid={`timeclock-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.badge > 0 && (
              <span className="px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {/* Today / History Tab */}
        {(activeTab === 'today' || activeTab === 'history') && (
          <div>
            {/* Filters */}
            <div className="p-4 border-b border-border flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search staff..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              {activeTab === 'history' && (
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={dateFilter.from}
                    onChange={(e) => setDateFilter(prev => ({ ...prev, from: e.target.value }))}
                    className="w-36"
                    placeholder="From"
                  />
                  <Input
                    type="date"
                    value={dateFilter.to}
                    onChange={(e) => setDateFilter(prev => ({ ...prev, to: e.target.value }))}
                    className="w-36"
                    placeholder="To"
                  />
                </div>
              )}
            </div>

            {/* Entries Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Staff</th>
                    {activeTab === 'history' && (
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Date</th>
                    )}
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Clock In</th>
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Clock Out</th>
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Hours</th>
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Device</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.length === 0 ? (
                    <tr>
                      <td colSpan={activeTab === 'history' ? 7 : 6} className="p-8 text-center text-muted-foreground">
                        No entries found
                      </td>
                    </tr>
                  ) : (
                    filteredEntries.map(entry => {
                      const status = entry.clock_out_time ? 'completed' : 'working';
                      const statusStyle = STATUS_COLORS[entry.is_late ? 'late' : status];
                      
                      return (
                        <tr key={entry.id} className="border-b border-border hover:bg-muted/30">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <User className="w-4 h-4 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium text-foreground">{entry.staff_name}</p>
                                <p className="text-xs text-muted-foreground capitalize">{entry.staff_role}</p>
                              </div>
                            </div>
                          </td>
                          {activeTab === 'history' && (
                            <td className="p-4 text-sm text-foreground">{entry.date}</td>
                          )}
                          <td className="p-4">
                            <div>
                              <p className="text-sm text-foreground">{formatTime(entry.clock_in_time)}</p>
                              {entry.early_clock_in && (
                                <p className="text-xs text-purple-600 dark:text-purple-400">Early (unpaid until {entry.scheduled_start})</p>
                              )}
                            </div>
                          </td>
                          <td className="p-4 text-sm text-foreground">{formatTime(entry.clock_out_time)}</td>
                          <td className="p-4 text-sm text-foreground font-medium">
                            {formatHours(entry.paid_hours || entry.current_hours)}
                            {entry.overtime_hours > 0 && (
                              <span className="text-xs text-amber-600 dark:text-amber-400 ml-1">
                                (+{entry.overtime_hours.toFixed(1)} OT)
                              </span>
                            )}
                          </td>
                          <td className="p-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                              {entry.is_late && <AlertTriangle className="w-3 h-3" />}
                              {entry.is_late ? `Late ${entry.minutes_late}m` : statusStyle.label}
                            </span>
                          </td>
                          <td className="p-4 text-sm text-muted-foreground">{entry.device_name || 'N/A'}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Devices Tab */}
        {activeTab === 'devices' && (
          <div className="p-4">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {devices.length === 0 ? (
                <div className="col-span-full p-8 text-center text-muted-foreground">
                  No registered devices
                </div>
              ) : (
                devices.map(device => (
                  <div 
                    key={device.id} 
                    className={`p-4 rounded-xl border ${device.is_active ? 'border-border' : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10'}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Smartphone className={`w-5 h-5 ${device.is_active ? 'text-primary' : 'text-red-500'}`} />
                        <span className="font-medium text-foreground">{device.device_name}</span>
                      </div>
                      {device.is_active ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">Active</span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">Inactive</span>
                      )}
                    </div>
                    <div className="space-y-1 text-sm">
                      <p className="text-muted-foreground">
                        <span className="text-foreground font-medium">{device.staff_name}</span> ({device.staff_role})
                      </p>
                      <p className="text-muted-foreground">Type: {device.device_type}</p>
                      <p className="text-muted-foreground">Registered: {formatDate(device.registered_at)}</p>
                      {device.last_used_at && (
                        <p className="text-muted-foreground">Last used: {formatDate(device.last_used_at)}</p>
                      )}
                    </div>
                    {device.is_active && (
                      <div className="flex gap-2 mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleClearDeviceRegistration(device.staff_id, device.staff_name)}
                          className="flex-1 text-blue-600 border-blue-300 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-700 dark:hover:bg-blue-900/20"
                        >
                          Clear & Re-register
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeactivateDevice(device.id)}
                          className="flex-1"
                        >
                          Deactivate
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Alerts Tab */}
        {activeTab === 'alerts' && (
          <div className="p-4">
            {alerts.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                <p>No active alerts</p>
              </div>
            ) : (
              <div className="space-y-3">
                {alerts.map(alert => (
                  <div 
                    key={alert.id} 
                    className={`p-4 rounded-xl border flex items-start justify-between gap-4
                      ${alert.type === 'late_clock_in' ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10' : ''}
                      ${alert.type === 'duplicate_device' ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      {alert.type === 'late_clock_in' && <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />}
                      {alert.type === 'duplicate_device' && <Shield className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />}
                      <div>
                        <p className="font-medium text-foreground">
                          {alert.type === 'late_clock_in' && 'Late Clock-In'}
                          {alert.type === 'duplicate_device' && 'Duplicate Device Attempt'}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {alert.type === 'late_clock_in' && `${alert.staff_name} clocked in ${alert.minutes_late} minutes late at ${alert.clock_in_time}`}
                          {alert.type === 'duplicate_device' && `${alert.staff_name} tried to register a device already used by ${alert.existing_staff}`}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">{formatDate(alert.created_at || alert.sent_at)}</p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {alert.type === 'duplicate_device' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-blue-600 border-blue-300 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-700 dark:hover:bg-blue-900/20"
                          onClick={() => handleClearDeviceRegistration(alert.staff_id, alert.staff_name)}
                        >
                          Clear & Allow Re-register
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDismissAlert(alert.id)}
                      >
                        Dismiss
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Payroll Report Tab */}
        {activeTab === 'payroll' && (
          <div className="p-4">
            {/* Period Selector */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1">
                <label className="block text-sm font-medium text-foreground mb-1">Start Date</label>
                <Input
                  type="date"
                  value={payrollPeriod.start}
                  onChange={(e) => setPayrollPeriod(p => ({ ...p, start: e.target.value }))}
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-foreground mb-1">End Date</label>
                <Input
                  type="date"
                  value={payrollPeriod.end}
                  onChange={(e) => setPayrollPeriod(p => ({ ...p, end: e.target.value }))}
                />
              </div>
              <div className="flex items-end gap-2">
                <Button onClick={fetchPayrollReport} disabled={payrollLoading}>
                  {payrollLoading ? 'Loading...' : 'Generate Report'}
                </Button>
                {payrollReport && (
                  <Button variant="outline" onClick={downloadPayrollCSV} className="gap-2">
                    <Download className="w-4 h-4" />
                    Export CSV
                  </Button>
                )}
              </div>
            </div>

            {/* Report Content */}
            {payrollLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : payrollReport ? (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                    <p className="text-sm text-blue-600 dark:text-blue-400">Total Staff</p>
                    <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{payrollReport.summary.total_staff}</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
                    <p className="text-sm text-green-600 dark:text-green-400">Regular Hours</p>
                    <p className="text-2xl font-bold text-green-700 dark:text-green-300">{payrollReport.summary.total_regular_hours}</p>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4">
                    <p className="text-sm text-amber-600 dark:text-amber-400">Overtime Hours</p>
                    <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{payrollReport.summary.total_overtime_hours}</p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
                    <p className="text-sm text-purple-600 dark:text-purple-400">Total Hours</p>
                    <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{payrollReport.summary.total_hours}</p>
                  </div>
                </div>

                {/* Staff Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left p-3 font-medium text-foreground">Staff Name</th>
                        <th className="text-left p-3 font-medium text-foreground">Role</th>
                        <th className="text-right p-3 font-medium text-foreground">Regular Hrs</th>
                        <th className="text-right p-3 font-medium text-foreground">Overtime</th>
                        <th className="text-right p-3 font-medium text-foreground">Total Hrs</th>
                        <th className="text-right p-3 font-medium text-foreground">Shifts</th>
                        <th className="text-right p-3 font-medium text-foreground">Late</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payrollReport.staff.map(staff => (
                        <tr key={staff.staff_id} className="border-b border-border hover:bg-muted/50">
                          <td className="p-3 font-medium text-foreground">{staff.staff_name}</td>
                          <td className="p-3 text-muted-foreground capitalize">{staff.role}</td>
                          <td className="p-3 text-right text-foreground">{staff.regular_hours}</td>
                          <td className="p-3 text-right text-amber-600 dark:text-amber-400">{staff.overtime_hours}</td>
                          <td className="p-3 text-right font-medium text-foreground">{staff.total_hours}</td>
                          <td className="p-3 text-right text-muted-foreground">{staff.shifts_worked}</td>
                          <td className="p-3 text-right">
                            {staff.late_count > 0 ? (
                              <span className="text-red-600 dark:text-red-400">{staff.late_count}</span>
                            ) : (
                              <span className="text-green-600 dark:text-green-400">0</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-muted/50 font-medium">
                        <td className="p-3 text-foreground">TOTALS</td>
                        <td className="p-3"></td>
                        <td className="p-3 text-right text-foreground">{payrollReport.summary.total_regular_hours}</td>
                        <td className="p-3 text-right text-amber-600 dark:text-amber-400">{payrollReport.summary.total_overtime_hours}</td>
                        <td className="p-3 text-right text-foreground">{payrollReport.summary.total_hours}</td>
                        <td className="p-3 text-right text-muted-foreground">{payrollReport.summary.total_shifts}</td>
                        <td className="p-3"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {payrollReport.staff.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No time clock entries found for this period</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Select a date range and click &ldquo;Generate Report&rdquo;</p>
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && settings && (
          <SettingsPanel settings={settings} onSave={handleSaveSettings} />
        )}
      </div>
    </div>
  );
}

// Settings Panel Component
function SettingsPanel({ settings, onSave }) {
  const [localSettings, setLocalSettings] = useState(settings);
  const [saving, setSaving] = useState(false);

  const handleChange = (key, value) => {
    setLocalSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(localSettings);
    setSaving(false);
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="text-lg font-medium text-foreground mb-4">Device Registration</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Max Devices per Staff</label>
            <Input
              type="number"
              min="1"
              max="5"
              value={localSettings.max_devices_per_staff || 1}
              onChange={(e) => handleChange('max_devices_per_staff', parseInt(e.target.value))}
            />
            <p className="text-xs text-muted-foreground mt-1">Number of devices a staff member can register</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Device Cooldown (Days)</label>
            <Input
              type="number"
              min="1"
              max="30"
              value={localSettings.device_cooldown_days || 14}
              onChange={(e) => handleChange('device_cooldown_days', parseInt(e.target.value))}
            />
            <p className="text-xs text-muted-foreground mt-1">Wait period before registering a new device</p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-foreground mb-4">Wi-Fi Verification</h3>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="require_wifi"
              checked={localSettings.require_wifi || false}
              onChange={(e) => handleChange('require_wifi', e.target.checked)}
              className="w-4 h-4 rounded border-border"
            />
            <label htmlFor="require_wifi" className="text-sm text-foreground">Require Cafe Wi-Fi for clock in/out</label>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Allowed Wi-Fi Networks</label>
            <Input
              value={(localSettings.allowed_wifi_ssids || []).join(', ')}
              onChange={(e) => handleChange('allowed_wifi_ssids', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              placeholder="BulaccinoCafe, Bulaccino-Staff"
            />
            <p className="text-xs text-muted-foreground mt-1">Comma-separated list of allowed network names</p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-foreground mb-4">Clock-In Rules</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Early Clock-In Window (Minutes)</label>
            <Input
              type="number"
              min="0"
              max="60"
              value={localSettings.early_clock_in_minutes || 15}
              onChange={(e) => handleChange('early_clock_in_minutes', parseInt(e.target.value))}
            />
            <p className="text-xs text-muted-foreground mt-1">How early staff can clock in before shift (unpaid)</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Late Threshold (Minutes)</label>
            <Input
              type="number"
              min="0"
              max="30"
              value={localSettings.late_threshold_minutes || 5}
              onChange={(e) => handleChange('late_threshold_minutes', parseInt(e.target.value))}
            />
            <p className="text-xs text-muted-foreground mt-1">Grace period before marking as late</p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-foreground mb-4">Overtime Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="overtime_enabled"
              checked={localSettings.overtime_enabled || false}
              onChange={(e) => handleChange('overtime_enabled', e.target.checked)}
              className="w-4 h-4 rounded border-border"
            />
            <label htmlFor="overtime_enabled" className="text-sm text-foreground">Enable overtime tracking</label>
          </div>
          {localSettings.overtime_enabled && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Daily Overtime After (Hours)</label>
                <Input
                  type="number"
                  min="1"
                  max="24"
                  step="0.5"
                  value={localSettings.overtime_daily_hours || 8}
                  onChange={(e) => handleChange('overtime_daily_hours', parseFloat(e.target.value))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Weekly Overtime After (Hours)</label>
                <Input
                  type="number"
                  min="1"
                  max="168"
                  step="1"
                  value={localSettings.overtime_weekly_hours || 40}
                  onChange={(e) => handleChange('overtime_weekly_hours', parseFloat(e.target.value))}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-foreground mb-4">Break Requirements</h3>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="breaks_enabled"
              checked={localSettings.breaks_enabled || false}
              onChange={(e) => handleChange('breaks_enabled', e.target.checked)}
              className="w-4 h-4 rounded border-border"
            />
            <label htmlFor="breaks_enabled" className="text-sm text-foreground">Enable mandatory break tracking</label>
          </div>
          {localSettings.breaks_enabled && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Break Required After (Hours)</label>
                <Input
                  type="number"
                  min="1"
                  max="12"
                  step="0.5"
                  value={localSettings.break_after_hours || 5}
                  onChange={(e) => handleChange('break_after_hours', parseFloat(e.target.value))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Break Duration (Minutes)</label>
                <Input
                  type="number"
                  min="10"
                  max="120"
                  value={localSettings.break_duration_minutes || 30}
                  onChange={(e) => handleChange('break_duration_minutes', parseInt(e.target.value))}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="pt-4 border-t border-border">
        <Button onClick={handleSave} disabled={saving} data-testid="save-timeclock-settings-btn">
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
}
